/**
 * Efectos de celebración (confeti, fuegos artificiales, destellos).
 * @module utils/Celebrations
 */

import { pageTouch } from './Ui.js';

const CELEBRATION_TOUCH = { overlayIcon: 'celebration' };

/**
 * Texto u ícono del overlay de `pageTouch` durante la celebración.
 * @param {{ overlayText?: string, overlayIcon?: string }} source
 */
function buildTouchOpts(source) {
    const t = { ...CELEBRATION_TOUCH };
    if (typeof source.overlayText === 'string' && source.overlayText.trim()) {
        t.overlayText = source.overlayText.trim();
    }
    if (typeof source.overlayIcon === 'string' && source.overlayIcon.trim()) {
        t.overlayIcon = source.overlayIcon.trim();
    }
    return t;
}

/**
 * Separa opciones del efecto de las de touch (overlayText, overlayIcon, noPageTouch).
 * @param {Record<string, unknown>} options
 */
function splitCelebrateOptions(options) {
    const noPageTouch = options.noPageTouch ?? false;
    const effectOpts = { ...options };
    delete effectOpts.noPageTouch;
    delete effectOpts.overlayText;
    delete effectOpts.overlayIcon;
    return {
        noPageTouch,
        touchOpts: buildTouchOpts(options),
        effectOpts,
    };
}

const CELEBRATION_COLORS = [
    '#ffb265',
    '#ffd700',
    '#00c566',
    '#3b82f6',
    '#a855f7',
    '#ff3b9d',
    '#ff3b3b',
    '#ffffff',
];

/**
 * Reduce motion → celebración muy suave en lugar de partículas.
 * @returns {boolean}
 */
function prefersReducedCelebrationMotion() {
    try {
        return (
            typeof globalThis.matchMedia === 'function' &&
            globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
    } catch {
        return false;
    }
}

/** @returns {boolean} true si se aplicó modo reducido y no hay más que hacer */
function bailReducedCelebrationFlash(touchOpts = CELEBRATION_TOUCH) {
    if (!prefersReducedCelebrationMotion()) return false;
    celebrateSoftFlash();
    pageTouch(true, true, { ...touchOpts, releaseAfterMs: 560 });
    return true;
}

/**
 * Capa fullscreen fija, sin interferir con toques (`pointer-events: none`).
 */
function createCelebrationRoot() {
    const root = document.createElement('div');
    root.className = 'celebration-layer';
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = [
        'position:fixed',
        'inset:0',
        'pointer-events:none',
        'z-index:9500',
        'overflow:hidden',
    ].join(';');
    document.body.appendChild(root);
    return root;
}

function celebrateSoftFlash(duration = 340) {
    const root = createCelebrationRoot();
    root.style.background =
        'radial-gradient(circle at 50% 40%,rgba(255,210,96,0.35),transparent 62%)';
    const anim = root.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }], {
        duration,
        easing: 'ease-out',
        fill: 'forwards',
    });
    anim.onfinish = () => root.remove();
}

/**
 * @param {'strip'|'rod'} shape
 * @returns {HTMLSpanElement}
 */
function createConfettiParticle(shape = 'rod') {
    const el = document.createElement('span');
    el.style.position = 'absolute';
    el.style.borderRadius = '2px';
    el.style.pointerEvents = 'none';
    if (shape === 'strip') {
        el.style.width = '10px';
        el.style.height = '14px';
    } else {
        el.style.width = '7px';
        el.style.height = '11px';
    }
    el.style.background =
        CELEBRATION_COLORS[Math.floor(Math.random() * CELEBRATION_COLORS.length)];
    el.style.opacity = String(0.85 + Math.random() * 0.15);
    el.style.boxShadow = '0 1px 2px rgba(0,0,0,0.15)';
    return el;
}

/**
 * Ráfaga radial de confeti desde un punto (px).
 * @param {HTMLDivElement} root
 * @param {number} cx
 * @param {number} cy
 * @param {{ duration?: number, count?: number }} opts
 */
function confettiBurstAt(root, cx, cy, opts = {}) {
    const { duration = 1600, count = 44 } = opts;
    const rect = root.getBoundingClientRect();
    const w = rect.width || (typeof window !== 'undefined' ? window.innerWidth : 400);
    const h = rect.height || (typeof window !== 'undefined' ? window.innerHeight : 600);

    for (let i = 0; i < count; i++) {
        const p = createConfettiParticle('rod');
        const angle = Math.PI * 2 * (i / count) * (0.88 + Math.random() * 0.24);
        const thrust = Math.min(w, h) * (0.2 + Math.random() * 0.42);
        const vx = Math.cos(angle) * thrust;
        const vy = Math.sin(angle) * thrust;

        p.style.left = `${cx}px`;
        p.style.top = `${cy}px`;
        p.style.transform = 'translate(-50%, -50%)';
        root.appendChild(p);

        p.animate(
            [
                {
                    transform: 'translate(-50%, -50%) scale(1) rotate(0deg)',
                    opacity: 1,
                },
                {
                    transform: `translate(calc(-50% + ${vx}px), calc(-50% + ${vy}px)) scale(0.5) rotate(${180 + Math.random() * 420}deg)`,
                    opacity: 0,
                },
            ],
            {
                duration,
                delay: Math.random() * 120,
                easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                fill: 'forwards',
            },
        );
    }
}

/** Mismo easing que la lluvia: arranca más lenta y acelera hacia el final. */
const CONFETTI_RAIN_EASING = 'cubic-bezier(0.33, 0.06, 0.72, 1)';
/** Factor < 1 → caída un poco más rápida, sin cambiar la curva. */
const CONFETTI_RAIN_FALL_SPEED = 0.82;

/**
 * Confetti que cae desde arriba (lluvia con balanceo opcional).
 * Durante **`delaySpread`** (ms) se siguen spawn-eando copos; hasta el último fade-out siguen cayendo nuevos y viejos.
 * @param {{ duration?: number, count?: number, sway?: boolean, delaySpread?: number, noPageTouch?: boolean, overlayText?: string, overlayIcon?: string }} [options]
 */
export function celebrateConfettiRain(options = {}) {
    const { noPageTouch, touchOpts, effectOpts } = splitCelebrateOptions(options);
    const { duration = 5800, count = 150, sway = true, delaySpread = 3000 } = effectOpts;

    if (bailReducedCelebrationFlash(touchOpts)) return;

    if (!noPageTouch) pageTouch(true, true, touchOpts);
    const root = createCelebrationRoot();
    const h = typeof window !== 'undefined' ? window.innerHeight : 800;

    const fallEasing = CONFETTI_RAIN_EASING;
    const fallSpeed = CONFETTI_RAIN_FALL_SPEED;

    /**
     * @param {number} delay — retraso antes de empezar la caída (ms)
     */
    function addRainParticle(delay) {
        const p = createConfettiParticle(Math.random() > 0.45 ? 'strip' : 'rod');
        const startXPercent = Math.random() * 100;
        const fallDuration = duration * (0.64 + Math.random() * 0.28) * fallSpeed;

        p.style.left = `${startXPercent}vw`;
        p.style.top = '-24px';
        p.style.transform = `translate(-50%, 0) rotate(${Math.random() * 360}deg)`;
        root.appendChild(p);

        const swayPx = sway ? -52 + Math.random() * 104 : 0;
        const rotations = Math.random() * 1260;
        const fadeStart = 0.66 + Math.random() * 0.16;

        p.animate(
            [
                {
                    transform: 'translate(-50%, 0) rotate(0deg)',
                },
                {
                    transform: `translate(calc(-50% + ${swayPx}px), ${h + 64}px) rotate(${rotations}deg)`,
                },
            ],
            {
                duration: fallDuration,
                delay,
                easing: fallEasing,
                fill: 'forwards',
            },
        );

        p.animate(
            [
                { opacity: 1, offset: 0 },
                { opacity: 1, offset: fadeStart },
                { opacity: 0, offset: 1 },
            ],
            {
                duration: fallDuration,
                delay,
                easing: 'linear',
                fill: 'forwards',
            },
        );
    }

    /* Ráfaga inicial para que empiece bien poblado. */
    const initialBurst = Math.max(22, Math.min(72, Math.floor(count * 0.32)));
    for (let i = 0; i < initialBurst; i++) {
        addRainParticle(Math.random() * 360);
    }

    const tSpawn0 = performance.now();
    /** Copos nuevos hasta que pase **`delaySpread`** (ms desde ahora). */
    const spawnSpreadMs = delaySpread;

    function spawnTick() {
        const elapsed = performance.now() - tSpawn0;
        if (elapsed >= spawnSpreadMs) {
            return;
        }
        const extra = Math.max(0, Math.floor((count - 120) / 90));
        const batchSize = 2 + Math.floor(Math.random() * 3) + extra;
        for (let j = 0; j < batchSize; j++) {
            addRainParticle(0);
        }
        setTimeout(spawnTick, 36 + Math.random() * 52);
    }
    spawnTick();

    /** Últimos copos aparecen casi en `spawnSpreadMs`; tiempo máx. de caída + fade. */
    const maxFall = duration * 0.92 * fallSpeed;
    const cleanupDelay = spawnSpreadMs + maxFall + 480;
    if (!noPageTouch) {
        setTimeout(() => {
            root.remove();
            pageTouch(false);
        }, cleanupDelay);
    } else {
        setTimeout(() => root.remove(), cleanupDelay);
    }
}

/**
 * Explosión radial desde abajo del viewport o centro.
 * @param {{ duration?: number, count?: number, origin?: 'bottom'|'center', noPageTouch?: boolean, overlayText?: string, overlayIcon?: string }} [options]
 */
export function celebrateConfettiBurst(options = {}) {
    const { noPageTouch, touchOpts, effectOpts } = splitCelebrateOptions(options);
    const { duration = 1600, count = 56, origin = 'bottom' } = effectOpts;

    if (bailReducedCelebrationFlash(touchOpts)) return;

    if (!noPageTouch) pageTouch(true, true, touchOpts);
    const root = createCelebrationRoot();
    const w = typeof window !== 'undefined' ? window.innerWidth : 400;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const cx = w * 0.5;
    const cy =
        origin === 'center'
            ? h * 0.42
            : h - Math.min(120, Math.max(56, h * 0.12));

    confettiBurstAt(root, cx, cy, { duration, count });

    const cleanupDelay = duration + 900;
    if (!noPageTouch) {
        setTimeout(() => {
            root.remove();
            pageTouch(false);
        }, cleanupDelay);
    } else {
        setTimeout(() => root.remove(), cleanupDelay);
    }
}

/**
 * Varios bursts en distintos puntos (fuegos artificiales).
 * @param {{
 *   waves?: number,
 *   particlesPerWave?: number,
 *   burstDuration?: number,
 *   waveGapMin?: number,
 *   waveGapMax?: number,
 *   noPageTouch?: boolean,
 *   overlayText?: string,
 *   overlayIcon?: string,
 * }} [options]
 */
export function celebrateConfettiFireworks(options = {}) {
    const { noPageTouch, touchOpts, effectOpts } = splitCelebrateOptions(options);
    const {
        waves = 5,
        particlesPerWave = 46,
        burstDuration = 1750,
        waveGapMin = 380,
        waveGapMax = 920,
    } = effectOpts;

    if (bailReducedCelebrationFlash(touchOpts)) return;

    if (!noPageTouch) pageTouch(true, true, touchOpts);
    const root = createCelebrationRoot();
    const w = typeof window !== 'undefined' ? window.innerWidth : 400;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;

    const marginX = w * 0.1;
    const marginTop = h * 0.08;
    const skyHeight = h * 0.48;

    let t = 0;
    let maxEnd = 0;

    for (let wv = 0; wv < waves; wv++) {
        const delay = t;
        const cx = marginX + Math.random() * (w - 2 * marginX);
        const cy = marginTop + Math.random() * skyHeight;

        setTimeout(() => {
            confettiBurstAt(root, cx, cy, {
                duration: burstDuration,
                count: particlesPerWave,
            });
        }, delay);

        t += waveGapMin + Math.random() * (waveGapMax - waveGapMin);
        maxEnd = Math.max(maxEnd, delay + burstDuration + 80);
    }

    const cleanupDelay = maxEnd + 220;
    if (!noPageTouch) {
        setTimeout(() => {
            root.remove();
            pageTouch(false);
        }, cleanupDelay);
    } else {
        setTimeout(() => root.remove(), cleanupDelay);
    }
}

/**
 * Destellos radiales rápidos (menos elementos, muy “celebratorio”).
 * @param {{ duration?: number, count?: number, noPageTouch?: boolean, overlayText?: string, overlayIcon?: string }} [options]
 */
export function celebrateSparksBurst(options = {}) {
    const { noPageTouch, touchOpts, effectOpts } = splitCelebrateOptions(options);
    const { duration = 2000, count = 102 } = effectOpts;

    if (bailReducedCelebrationFlash(touchOpts)) return;

    if (!noPageTouch) pageTouch(true, true, touchOpts);
    const root = createCelebrationRoot();
    const w = typeof window !== 'undefined' ? window.innerWidth : 400;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const cx = w * 0.5;
    const cy = h * 0.38;

    for (let i = 0; i < count; i++) {
        const dot = document.createElement('span');
        dot.style.position = 'absolute';
        dot.style.width = `${4 + Math.random() * 5}px`;
        dot.style.height = dot.style.width;
        dot.style.borderRadius = '50%';
        dot.style.left = `${cx}px`;
        dot.style.top = `${cy}px`;
        dot.style.background =
            CELEBRATION_COLORS[(i + Math.floor(Math.random() * 3)) % CELEBRATION_COLORS.length];
        dot.style.boxShadow = '0 0 8px rgba(255,235,170,0.9)';
        root.appendChild(dot);

        const angle = Math.random() * Math.PI * 2;
        const r = Math.min(w, h) * (0.12 + Math.random() * 0.22);
        const vx = Math.cos(angle) * r;
        const vy = Math.sin(angle) * r;

        dot.animate(
            [
                { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
                {
                    transform: `translate(calc(-50% + ${vx}px), calc(-50% + ${vy}px)) scale(0.35)`,
                    opacity: 0,
                },
            ],
            {
                duration: duration * (0.75 + Math.random() * 0.35),
                easing: 'cubic-bezier(0.05, 0.9, 0.25, 1)',
                fill: 'forwards',
            },
        );
    }

    const cleanupDelay = duration + 720;
    if (!noPageTouch) {
        setTimeout(() => {
            root.remove();
            pageTouch(false);
        }, cleanupDelay);
    } else {
        setTimeout(() => root.remove(), cleanupDelay);
    }
}

/**
 * Lluvia larga + fuegos artificiales escalonados (máximo impacto).
 * @param {{
 *   rain?: { duration?: number, count?: number, sway?: boolean, delaySpread?: number },
 *   fireworks?: { waves?: number, particlesPerWave?: number, burstDuration?: number, waveGapMin?: number, waveGapMax?: number },
 *   fireworksDelay?: number,
 *   overlayText?: string,
 *   overlayIcon?: string,
 * }} [options]
 */
export function celebrateGrand(options = {}) {
    const touchOpts = buildTouchOpts(options);

    if (bailReducedCelebrationFlash(touchOpts)) return;

    const estimateReleaseMs = (o, delay) => {
        const rn = o.rain ?? {};
        const fw = o.fireworks ?? {};
        const rd = rn.duration ?? 5800;
        const rds = rn.delaySpread ?? 3000;
        const rainEnd = rds + rd * 0.92 * CONFETTI_RAIN_FALL_SPEED + 920;
        const w = fw.waves ?? 8;
        const gapMax = fw.waveGapMax ?? 920;
        const bd = fw.burstDuration ?? 1750;
        const fireworksSpan = w * gapMax + bd + 780;
        return Math.max(rainEnd, delay + fireworksSpan) + 320;
    };

    const { rain = {}, fireworks = {}, fireworksDelay = 750 } = options;

    pageTouch(true, true, {
        ...touchOpts,
        releaseAfterMs: estimateReleaseMs(options, fireworksDelay),
    });

    celebrateConfettiRain({ ...rain, noPageTouch: true });

    setTimeout(() => {
        celebrateConfettiFireworks({ ...fireworks, noPageTouch: true });
    }, fireworksDelay);
}

/**
 * Elegís variante: `'rain'`, `'burst'`, `'sparks'`, `'fireworks'`, `'grand'`.
 * @param {'rain'|'burst'|'sparks'|'fireworks'|'grand'} preset
 * @param {Record<string, unknown>} overrides — opciones del efecto; podés pasar **`overlayText`** (y opcional **`overlayIcon`**) para el overlay de `pageTouch`. En `grand`: también `rain`, `fireworks`, `fireworksDelay`.
 */
export function celebratePreset(preset, overrides = {}) {
    switch (preset) {
        case 'burst':
            celebrateConfettiBurst(overrides);
            break;
        case 'sparks':
            celebrateSparksBurst(overrides);
            break;
        case 'fireworks':
            celebrateConfettiFireworks(overrides);
            break;
        case 'grand':
            celebrateGrand(overrides);
            break;
        case 'rain':
        default:
            celebrateConfettiRain(overrides);
            break;
    }
}
